import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { MIME_TYPES } from "@excalidraw/common";

import { duplicateElements } from "@excalidraw/element/duplicate";
import { newFrameElement } from "@excalidraw/element/newElement";
import { getCommonBounds } from "@excalidraw/element/bounds";
import { addElementsToFrame } from "@excalidraw/element/frame";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { serializeLibraryAsJSON } from "../data/json";
import { useLibraryCache } from "../hooks/useLibraryItemSvg";
import { useScrollPosition } from "../hooks/useScrollPosition";

import { LibraryDropdownMenu } from "./LibraryMenuHeaderContent";

import "./LibraryMenuItems.scss";

import { useApp, useExcalidrawAppState, useExcalidrawSetAppState } from "./App";

import type {
  ExcalidrawProps,
  LibraryItem,
  LibraryItems,
  UIAppState,
} from "../types";
import { Dialog } from "./Dialog";
import { LibraryMenuSection } from "./LibraryMenuSection";
import { LibraryMenuSectionGrid } from "./LibraryMenuSection";
import DialogActionButton from "./DialogActionButton";
import { Tooltip } from "./Tooltip";
import {
  getSubscription,
  signOut,
  upgradeSubscription,
  useSession,
} from "./lib/auth-client";
import { Auth } from "./Auth";
import type { Subscription } from "@better-auth/stripe";
import { LoaderIcon } from "react-hot-toast";
import { BACKEND_URL } from "./lib/constant";

// const BACKEND_URL = "http://localhost:8787/api";

// using an odd number of items per batch so the rendering creates an irregular
// pattern which looks more organic
const ITEMS_RENDERED_PER_BATCH = 17;
// when render outputs cached we can render many more items per batch to
// speed it up
const CACHED_ITEMS_RENDERED_PER_BATCH = 64;

const DIALOG_STATE_KEY = "excalidraw-ai-dialog-state";
const USER_PROMPT_KEY = "excalidraw-ai-user-prompt";

const fetchTemplate = async () => {
  const response = await fetch(`${BACKEND_URL}/templates`, {
    credentials: "include",
  });
  const data = await response.json();
  return data;
};

const generateContent = async (userPrompt: string) => {
  const response = await fetch(`${BACKEND_URL}/ai/generate-content`, {
    method: "POST",
    body: JSON.stringify({ userPrompt }),
    credentials: "include",
  });
  const data = (await response.json()) as {
    elements: {
      textType: string;
      content: string;
    }[];
  };
  return data?.elements;
};

const saveTemplatesToLocalStorage = (templates: LibraryItems) => {
  localStorage.setItem("excalidraw-templates", JSON.stringify(templates));
};

const loadTemplatesFromLocalStorage = (): LibraryItems => {
  const savedTemplates = localStorage.getItem("excalidraw-templates");
  return savedTemplates ? JSON.parse(savedTemplates) : [];
};

type FallbackPattern = {
  pattern: RegExp;
  fallbacks: {
    from: string;
    to: string;
  }[];
};

const FALLBACK_PATTERNS: FallbackPattern[] = [
  {
    pattern: /POINT_(\d+)_/,
    fallbacks: [
      { from: "_DESCRIPTION", to: "_SUBTITLE" },
      { from: "_SUBTITLE", to: "_DESCRIPTION" },
    ],
  },
  // Add more patterns here as needed
  // Example:
  // {
  //   pattern: /SECTION_(\d+)_/,
  //   fallbacks: [
  //     { from: "_TITLE", to: "_HEADING" },
  //     { from: "_HEADING", to: "_TITLE" },
  //   ],
  // }
];

function getFallbackContent(
  elementLink: string,
  contentMap: Map<string, string>,
): string | null {
  for (const { pattern, fallbacks } of FALLBACK_PATTERNS) {
    const match = elementLink.match(pattern);
    if (match) {
      const [_, id] = match;
      const baseKey = elementLink.split("_").slice(0, -1).join("_");

      // Try each fallback pattern
      for (const { from, to } of fallbacks) {
        if (elementLink.endsWith(from)) {
          const fallbackKey = `${baseKey}${to}`;
          if (contentMap.has(fallbackKey)) {
            return contentMap.get(fallbackKey) || null;
          }
        }
      }
    }
  }
  return null;
}

export function swapElementsWithGeneratedContent(
  elements: ExcalidrawElement[],
  generatedContent: { textType: string; content: string }[],
) {
  const contentMap = new Map(
    generatedContent.map((item) => [item.textType, item.content]),
  );
  const frameContent = contentMap.get("TITLE") || "";

  return elements.map((element) => {
    if (element.type === "frame") {
      element.name = frameContent;
    }

    if (element.link) {
      // Check if we have the exact link match
      if (contentMap.has(element.link)) {
        return {
          ...element,
          text: contentMap.get(element.link),
          originalText: contentMap.get(element.link),
          link: null,
        };
      }

      // Try to find fallback content
      const fallbackContent = getFallbackContent(element.link, contentMap);
      if (fallbackContent) {
        return {
          ...element,
          text: fallbackContent,
          originalText: fallbackContent,
          link: null,
        };
      }
    }
    return element;
  });
}

type Step = "generate" | "select-template";

interface GeneratedHistoryItem {
  id: string;
  userPrompt: string;
  generatedContent: { textType: string; content: string }[];
  createdAt: string;
}

const saveGenerationsToLocalStorage = (generations: GeneratedHistoryItem[]) => {
  localStorage.setItem("excalidraw-generations", JSON.stringify(generations));
};

const loadGenerationsFromLocalStorage = (): GeneratedHistoryItem[] => {
  const savedGenerations = localStorage.getItem("excalidraw-generations");
  return savedGenerations ? JSON.parse(savedGenerations) : [];
};

const fetchGenerations = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/generations`, {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to fetch generations");
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching generations:", error);
    return [];
  }
};

const getTitleFromGeneratedContent = (
  content: { textType: string; content: string }[],
): string => {
  // First try to find a TITLE element
  const titleElement = content.find((item) => item.textType === "TITLE");
  if (titleElement) {
    return titleElement.content;
  }

  // If no TITLE, look for HEADING elements
  const headingElement = content.find(
    (item) =>
      item.textType.includes("HEADING") ||
      item.textType.includes("SECTION_TITLE"),
  );
  if (headingElement) {
    return headingElement.content;
  }

  // If no title or heading found, use the first text element
  const firstTextElement = content[0];
  if (firstTextElement) {
    return firstTextElement.content;
  }

  // Fallback to empty string if no content
  return "";
};

export default function LibraryMenuItemsCopy({
  isLoading,
  libraryItems,
  onAddToLibrary,
  onInsertLibraryItems,
  pendingElements,
  theme,
  id,
  libraryReturnUrl,
  onSelectItems,
  selectedItems,
}: {
  isLoading: boolean;
  libraryItems: LibraryItems;
  pendingElements: LibraryItem["elements"];
  onInsertLibraryItems: (libraryItems: LibraryItems) => void;
  onAddToLibrary: (elements: LibraryItem["elements"]) => void;
  libraryReturnUrl: ExcalidrawProps["libraryReturnUrl"];
  theme: UIAppState["theme"];
  id: string;
  selectedItems: LibraryItem["id"][];
  onSelectItems: (id: LibraryItem["id"][]) => void;
}) {
  const libraryContainerRef = useRef<HTMLDivElement>(null);
  const scrollPosition = useScrollPosition<HTMLDivElement>(libraryContainerRef);
  const [templates, setTemplates] = useState<LibraryItems>([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const { openDialog } = useExcalidrawAppState();
  const setExcalidrawAppState = useExcalidrawSetAppState();

  const [showAIGenerationDialog, setShowAIGenerationDialog] = useState(() => {
    const savedState = localStorage.getItem(DIALOG_STATE_KEY);
    if (openDialog?.name === "pixingDialog") {
      return true;
    }
    return savedState ? JSON.parse(savedState) : false;
  });
  const [generatedContent, setGeneratedContent] = useState<
    { textType: string; content: string }[] | null
  >(null);
  const [currentStep, setCurrentStep] = useState<Step>("generate");
  const [userPrompt, setUserPrompt] = useState(() => {
    const savedPrompt = localStorage.getItem(USER_PROMPT_KEY);
    return savedPrompt || "";
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [history, setHistory] = useState<GeneratedHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { data: session, isPending } = useSession();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState<
    "index" | "profile" | "history"
  >("index");
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<string | null>(
    null,
  );

  // Add effect to show auth dialog when no session
  useEffect(() => {
    if (!isPending && !session?.user) {
      setShowAIGenerationDialog(true);
    }
  }, [session?.user, isPending]);

  useEffect(() => {
    if (openDialog?.name === "pixingDialog") {
      setShowAIGenerationDialog(true);
    }
  }, [openDialog]);

  const loadTemplates = useCallback(async (forceFetch = false) => {
    try {
      setIsTemplatesLoading(true);
      if (!forceFetch) {
        const savedTemplates = loadTemplatesFromLocalStorage();
        if (savedTemplates.length > 0) {
          setTemplates(savedTemplates);
          setIsTemplatesLoading(false);
          return;
        }
      }
      const data = await fetchTemplate();
      setTemplates(data);
      saveTemplatesToLocalStorage(data);
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setIsTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // This effect has to be called only on first render, therefore  `scrollPosition` isn't in the dependency array
  useEffect(() => {
    if (scrollPosition > 0) {
      libraryContainerRef.current?.scrollTo(0, scrollPosition);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { svgCache } = useLibraryCache();
  const unpublishedItems = useMemo(
    () => libraryItems.filter((item) => item.status !== "published"),
    [libraryItems],
  );

  const publishedItems = useMemo(
    () => libraryItems.filter((item) => item.status === "published"),
    [libraryItems],
  );

  const isLibraryEmpty =
    !pendingElements.length &&
    !unpublishedItems.length &&
    !publishedItems.length &&
    !templates.length;

  const [lastSelectedItem, setLastSelectedItem] = useState<
    LibraryItem["id"] | null
  >(null);

  const getInsertedElements = useCallback(
    (id: string) => {
      let targetElements;
      if (selectedItems.includes(id)) {
        targetElements = [...libraryItems, ...templates].filter((item) =>
          selectedItems.includes(item.id),
        );
      } else {
        targetElements = [...libraryItems, ...templates].filter(
          (item) => item.id === id,
        );
      }
      return targetElements.map((item) => {
        return {
          ...item,
          // duplicate each library item before inserting on canvas to confine
          // ids and bindings to each library item. See #6465
          elements: duplicateElements({
            type: "everything",
            elements: item.elements,
            randomizeSeed: true,
          }).duplicatedElements,
        };
      });
    },
    [libraryItems, selectedItems, templates],
  );

  const onItemDrag = useCallback(
    (id: LibraryItem["id"], event: React.DragEvent) => {
      event.dataTransfer.setData(
        MIME_TYPES.excalidrawlib,
        serializeLibraryAsJSON(getInsertedElements(id)),
      );
    },
    [getInsertedElements],
  );

  const isItemSelected = useCallback(
    (id: LibraryItem["id"] | null) => {
      if (!id) {
        return false;
      }

      return selectedItems.includes(id);
    },
    [selectedItems],
  );

  const onAddToLibraryClick = useCallback(() => {
    onAddToLibrary(pendingElements);
  }, [pendingElements, onAddToLibrary]);

  const app = useApp();

  const loadHistory = useCallback(async (forceFetch = false) => {
    try {
      setIsLoadingHistory(true);
      if (!forceFetch) {
        const savedGenerations = loadGenerationsFromLocalStorage();
        if (savedGenerations && savedGenerations.length > 0) {
          setHistory(savedGenerations);
          setIsLoadingHistory(false);
          return;
        }
      }
      const generations = await fetchGenerations();
      if (generations && Array.isArray(generations)) {
        setHistory(generations);
        saveGenerationsToLocalStorage(generations);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error("Error loading history:", error);
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleGenerate = async () => {
    try {
      if (!userPrompt) {
        return;
      }
      setIsGenerating(true);
      const content = await generateContent(userPrompt);
      setGeneratedContent(content);

      // Add to history
      const newHistoryItem: GeneratedHistoryItem = {
        id: Date.now().toString(),
        userPrompt,
        generatedContent: content,
        createdAt: new Date().toISOString(),
      };
      setHistory((prev) => [newHistoryItem, ...prev].slice(0, 10));
      saveGenerationsToLocalStorage([newHistoryItem, ...history].slice(0, 10));

      setCurrentStep("select-template");
    } catch (error) {
      console.error("Error generating content:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (template && generatedContent) {
      const newElements = swapElementsWithGeneratedContent(
        [...template.elements],
        generatedContent,
      );

      const pastedElements = app.addElementsFromPasteOrLibrary({
        elements: newElements as readonly ExcalidrawElement[],
        files: null,
        position: "center",
        retainSeed: true,
      });

      // Check if any of the elements is already a frame
      const hasFrame = pastedElements.some(
        (element) => element.type === "frame",
      );

      let frame;
      if (!hasFrame) {
        // Create a frame around the pasted elements
        const [x1, y1, x2, y2] = getCommonBounds(
          pastedElements,
          app.scene.getNonDeletedElementsMap(),
        );
        const PADDING = 16;
        frame = newFrameElement({
          x: x1 - PADDING,
          y: y1 - PADDING,
          width: x2 - x1 + PADDING * 2,
          height: y2 - y1 + PADDING * 2,
          name: getTitleFromGeneratedContent(generatedContent),
        });

        // Add elements to frame
        const nextElements = addElementsToFrame(
          [...app.scene.getElementsIncludingDeleted(), frame],
          pastedElements,
          frame,
          app.state,
        );

        app.scene.replaceAllElements(nextElements);
      }

      // Select all the pasted elements and the frame if created
      const elementsToSelect = frame ? [frame] : pastedElements;
      const selectedElementIds = elementsToSelect.reduce((acc, element) => {
        acc[element.id] = true;
        return acc;
      }, {} as Record<string, true>);

      // Handle text WYSIWYG for text elements
      pastedElements.forEach((element) => {
        if (element.type === "text") {
          app.handleTextWysiwyg(element, {
            isExistingElement: true,
          });
        }
      });

      setExcalidrawAppState({
        selectedElementIds,
        selectedGroupIds: {},
        editingGroupId: null,
      });
      // Clear user prompt and localStorage
      setUserPrompt("");
      setGeneratedContent(null);
      setSelectedHistoryItem(null);
      setCurrentStep("generate");
      localStorage.removeItem(USER_PROMPT_KEY);
    }
    setShowAIGenerationDialog(false);
  };

  const handleHistoryItemClick = (item: GeneratedHistoryItem) => {
    setSelectedHistoryItem(item.id);
    setGeneratedContent(item.generatedContent);
    setCurrentStep("select-template");
    setShowHistory(false);
  };

  const handleAIGenerationClick = () => {
    setShowAIGenerationDialog(true);
    setGeneratedContent(null);
    setCurrentStep("generate");
    setShowHistory(false);
  };

  const handleUpgradeSubscription = async () => {
    try {
      setLoadingUpgrade(true);
      await upgradeSubscription();
    } catch (error) {
      console.error("Error upgrading subscription:", error);
    } finally {
      setLoadingUpgrade(false);
    }
  };

  const logout = async () => {
    await signOut();
    setShowAIGenerationDialog(false);
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      const { data, error } = await getSubscription();
      if (data && data.length > 0) {
        setSubscription(data[0]);
      }
    };
    fetchSubscription();
  }, []);

  const getPageTitle = () => {
    switch (currentPage) {
      case "profile":
        return "Profile";
      case "history":
        return "Generation History";
      default:
        return "Generate Drawing with AI";
    }
  };

  const renderPageContent = () => {
    if (!session?.user) {
      return (
        <div className="ai-generation-dialog__auth">
          <Auth
            onClose={() => setShowAIGenerationDialog(false)}
            onSuccess={() => {
              setCurrentPage("index");
            }}
          />
        </div>
      );
    }

    switch (currentPage) {
      case "profile":
        return (
          <div className="ai-generation-dialog__profile-page">
            <div className="ai-generation-dialog__profile-content">
              <div className="ai-generation-dialog__profile-section">
                <div className="ai-generation-dialog__profile-header">
                  <div className="ai-generation-dialog__profile-avatar">
                    {session?.user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="ai-generation-dialog__profile-info">
                    <h3>{session?.user?.name}</h3>
                    {subscription && (
                      <div className="ai-generation-dialog__subscription-badge">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2-6.3-4.6-6.3 4.6 2.3-7.2-6-4.4h7.6z" />
                        </svg>
                        <span>{subscription.plan || "Pro"} Plan</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ai-generation-dialog__profile-details">
                  <div className="ai-generation-dialog__detail-group">
                    <h4>Subscription Details</h4>
                    <div className="ai-generation-dialog__detail-items">
                      <div className="ai-generation-dialog__detail-item">
                        <span className="ai-generation-dialog__detail-label">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <path d="M22 6l-10 7L2 6" />
                          </svg>
                          Email
                        </span>
                        <span className="ai-generation-dialog__detail-value">
                          {session?.user?.email}
                        </span>
                      </div>
                      <div className="ai-generation-dialog__detail-item">
                        <span className="ai-generation-dialog__detail-label">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                            <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
                            <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
                          </svg>
                          Plan
                        </span>
                        <span className="ai-generation-dialog__detail-value">
                          {subscription?.plan?.toUpperCase() || "Free"}
                        </span>
                      </div>
                      {subscription && (
                        <div className="ai-generation-dialog__detail-item">
                          <span className="ai-generation-dialog__detail-label">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                              <path d="m9 12 2 2 4-4" />
                            </svg>
                            Status
                          </span>
                          <span className="ai-generation-dialog__detail-value">
                            <span
                              className={`status-badge status-${subscription.status.toLowerCase()}`}
                            >
                              {subscription.status}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="ai-generation-dialog__profile-actions">
                  <button
                    className="ai-generation-dialog__logout-button"
                    onClick={() => {
                      logout();
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case "history":
        return (
          <div className="ai-generation-dialog__history-page">
            <div className="ai-generation-dialog__history-content">
              <button
                className="ai-generation-dialog__refresh-button"
                onClick={() => loadHistory(true)}
                disabled={isLoadingHistory}
              >
                {isLoadingHistory ? "Refreshing..." : "Refresh"}
              </button>
              {isLoadingHistory ? (
                <div className="ai-generation-dialog__loading">
                  Loading history...
                </div>
              ) : history.length === 0 ? (
                <div className="ai-generation-dialog__empty">
                  No generations found
                </div>
              ) : (
                <div className="ai-generation-dialog__history-list">
                  {history?.map((item) => (
                    <div
                      key={item.id}
                      className="ai-generation-dialog__history-item"
                      onClick={() => handleHistoryItemClick(item)}
                    >
                      <div className="ai-generation-dialog__history-prompt">
                        {item.userPrompt}
                      </div>
                      <div className="ai-generation-dialog__history-time">
                        {new Date(item.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      default:
        return (
          <>
            <div
              className={`ai-generation-dialog__sidebar ${
                isSidebarCollapsed ? "collapsed" : ""
              }`}
            >
              <div className="ai-generation-dialog__sidebar-header">
                <h3>Recently Generated</h3>
                <Tooltip
                  label={isSidebarCollapsed ? "Recently Generated" : "Collapse"}
                >
                  <button
                    className="ai-generation-dialog__collapse-button"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    title={isSidebarCollapsed ? "Expand" : "Collapse"}
                  >
                    {isSidebarCollapsed ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-panel-left-open-icon lucide-panel-left-open"
                      >
                        <rect width="18" height="18" x="3" y="3" rx="2" />
                        <path d="M9 3v18" />
                        <path d="m14 9 3 3-3 3" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-panel-right-open-icon lucide-panel-right-open"
                      >
                        <rect width="18" height="18" x="3" y="3" rx="2" />
                        <path d="M15 3v18" />
                        <path d="m10 15-3-3 3-3" />
                      </svg>
                    )}
                  </button>
                </Tooltip>
              </div>
              {!isSidebarCollapsed && (
                <div className="ai-generation-dialog__sidebar-content">
                  <div className="ai-generation-dialog__history-section">
                    <button
                      className="ai-generation-dialog__create-new-button"
                      onClick={() => {
                        setSelectedHistoryItem(null);
                        setGeneratedContent(null);
                        setCurrentStep("generate");
                        setUserPrompt("");
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Create New
                    </button>
                    {isLoadingHistory ? (
                      <div className="ai-generation-dialog__loading">
                        Loading history...
                      </div>
                    ) : history.length === 0 ? (
                      <div className="ai-generation-dialog__empty">
                        No generations found
                      </div>
                    ) : (
                      <div className="ai-generation-dialog__history-list">
                        {history?.map((item) => (
                          <div
                            key={item.id}
                            className={`ai-generation-dialog__history-item ${
                              selectedHistoryItem === item.id ? "selected" : ""
                            }`}
                            onClick={() => handleHistoryItemClick(item)}
                          >
                            <div className="ai-generation-dialog__history-prompt">
                              {item.userPrompt}
                            </div>
                            <div className="ai-generation-dialog__history-time">
                              {new Date(item.createdAt).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="ai-generation-dialog__sidebar-footer">
                <button
                  className="ai-generation-dialog__profile-button"
                  onClick={() => setCurrentPage("profile")}
                  title="View Profile"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  {!isSidebarCollapsed && <span>Profile</span>}
                </button>
              </div>
            </div>
            <div className="ai-generation-dialog__content">
              <div className="ai-generation-stepper">
                <div className="ai-generation-stepper__steps">
                  <div
                    className={`ai-generation-stepper__step ${
                      currentStep === "generate" ? "active" : ""
                    }`}
                    onClick={() => setCurrentStep("generate")}
                  >
                    <div className="ai-generation-stepper__step-number">1</div>
                    <div className="ai-generation-stepper__step-title">
                      Generate Content
                    </div>
                  </div>
                  <div
                    className={`ai-generation-stepper__step ${
                      currentStep === "select-template" ? "active" : ""
                    }`}
                  >
                    <div className="ai-generation-stepper__step-number">2</div>
                    <div className="ai-generation-stepper__step-title">
                      Select Template
                    </div>
                  </div>
                </div>

                <div className="ai-generation-stepper__content">
                  {currentStep === "generate" ? (
                    <div className="ai-generation-stepper__generate">
                      <div className="ai-generation-stepper__input">
                        <label htmlFor="prompt">
                          Describe what you want to generate
                        </label>
                        <textarea
                          id="prompt"
                          value={userPrompt}
                          onChange={(e) => setUserPrompt(e.target.value)}
                          placeholder="e.g., A flowchart showing the process of user authentication"
                          rows={4}
                          disabled={isGenerating}
                        />
                      </div>

                      <div className="ai-generation-stepper__actions">
                        <DialogActionButton
                          label="Cancel"
                          onClick={() => setShowAIGenerationDialog(false)}
                        />
                        {!subscription ? (
                          <DialogActionButton
                            label={
                              loadingUpgrade
                                ? "Redirecting to payment page..."
                                : "Upgrade to Generate"
                            }
                            onClick={handleUpgradeSubscription}
                            actionType="primary"
                            disabled={loadingUpgrade}
                          />
                        ) : (
                          <DialogActionButton
                            label={isGenerating ? "Generating..." : "Generate"}
                            isLoading={isGenerating}
                            onClick={handleGenerate}
                            actionType="primary"
                            disabled={!userPrompt || isGenerating}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="ai-generation-stepper__templates">
                      <h3>Select a Template</h3>
                      <LibraryMenuSectionGrid>
                        <LibraryMenuSection
                          itemsRenderedPerBatch={64}
                          items={templates as any}
                          onItemSelectToggle={() => {}}
                          onItemDrag={() => {}}
                          onClick={(id) => id && handleTemplateSelect(id)}
                          isItemSelected={() => false}
                          svgCache={svgCache}
                        />
                      </LibraryMenuSectionGrid>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  useEffect(() => {
    localStorage.setItem(
      DIALOG_STATE_KEY,
      JSON.stringify(showAIGenerationDialog),
    );
  }, [showAIGenerationDialog]);

  useEffect(() => {
    localStorage.setItem(USER_PROMPT_KEY, userPrompt);
  }, [userPrompt]);

  const handleDialogClose = () => {
    setShowAIGenerationDialog(false);
    if (generatedContent) {
      setUserPrompt("");
      localStorage.removeItem(USER_PROMPT_KEY);
    }
  };

  return (
    <div className="library-menu-items-container">
      {!isLibraryEmpty && (
        <LibraryDropdownMenu
          selectedItems={selectedItems}
          onSelectItems={onSelectItems}
          className="library-menu-dropdown-container--in-heading"
        />
      )}

      <div className="library-menu-items-container__ai-section">
        <button
          className="library-menu-items-container__ai-button primary"
          onClick={handleAIGenerationClick}
          title="Generate Drawing with AI"
          disabled={isPending}
        >
          <span>Generate Drawing with AI</span>
          {isPending && (
            <div className="library-menu-items-container__ai-button-loader">
              <LoaderIcon />
            </div>
          )}
        </button>
      </div>

      {showAIGenerationDialog && (
        <Dialog
          isFullscreen={!session?.user}
          onCloseRequest={handleDialogClose}
          title={getPageTitle()}
          size="wide"
          showBackButton={currentPage !== "index"}
          onBackClick={() => setCurrentPage("index")}
          islandClassName="no-padding"
          closeOnClickOutside={false}
          showCloseButton={session?.user?.id ? true : false}
        >
          <div className="ai-generation-dialog">{renderPageContent()}</div>
        </Dialog>
      )}
    </div>
  );
}
